import { useState, useRef, useEffect } from 'react';
import type {
  FaptaType,
  PVExtracted,
  SemnaturaContravenient,
} from '@anuleaza/shared';

type FieldType = 'text' | 'textarea' | 'number' | 'select-tip' | 'select-semn' | 'select-bool';

// Each row maps a label to a dotted path inside PVExtracted plus the editor type.
// We keep this static rather than deriving from the schema so the display order
// matches the legal review flow (identification → fapta → încadrare → sancțiune).
interface Row {
  label: string;
  path: string;
  type: FieldType;
  hot?: boolean;
}

interface Props {
  extracted: PVExtracted;
  confidence: number;
  /** Called with the full updated PVExtracted on each successful edit. */
  onChange?: (next: PVExtracted) => void;
  /** Disable interaction while a save mutation is in flight or after analyze. */
  disabled?: boolean;
}

const TIP_OPTIONS: Array<{ value: FaptaType; label: string }> = [
  { value: 'viteză', label: 'Viteză' },
  { value: 'parcare', label: 'Parcare/Staționare' },
  { value: 'trecere_rosu', label: 'Trecere pe roșu' },
  { value: 'alcoolemie', label: 'Alcoolemie' },
  { value: 'altele', label: 'Altele' },
];

const SEMN_OPTIONS: Array<{ value: SemnaturaContravenient; label: string }> = [
  { value: 'prezentă', label: 'prezentă' },
  { value: 'absentă', label: 'absentă' },
  { value: 'refuzat', label: 'refuzat' },
];

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ro-RO', { dateStyle: 'medium', timeStyle: 'short' });
}

// Read/write helpers for dotted-path JSON access. Two levels deep is enough
// for PVExtracted's flat-ish shape.
function getPath(obj: PVExtracted, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur && typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return undefined;
    }
  }
  return cur;
}

function setPath(obj: PVExtracted, path: string, value: unknown): PVExtracted {
  const parts = path.split('.');
  // Structured clone preserves null/undefined and avoids reference aliasing.
  const out = JSON.parse(JSON.stringify(obj)) as PVExtracted;
  let cur: Record<string, unknown> = out as unknown as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]!;
    if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {};
    cur = cur[k] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
  return out;
}

export function ExtractedFieldList({ extracted, confidence, onChange, disabled }: Props) {
  const rows: Row[] = [
    { label: 'Serie & număr', path: 'serie_numar', type: 'text' },
    { label: 'Data emiterii', path: 'data_emitere', type: 'text' },
    { label: 'Agent constatator', path: 'agent_constatator.nume', type: 'text' },
    { label: 'Unitate', path: 'agent_constatator.unitate', type: 'text' },
    { label: 'Fapta', path: 'fapta.descriere', type: 'textarea', hot: true },
    { label: 'Locație', path: 'fapta.locatie', type: 'text' },
    { label: 'Tip faptă', path: 'fapta.tip', type: 'select-tip' },
    { label: 'Act normativ', path: 'incadrare_juridica.act_normativ', type: 'text', hot: true },
    { label: 'Articol', path: 'incadrare_juridica.articol', type: 'text', hot: true },
    { label: 'Alineat', path: 'incadrare_juridica.alineat', type: 'text' },
    { label: 'Literă', path: 'incadrare_juridica.litera', type: 'text' },
    { label: 'Amendă (lei)', path: 'sanctiune.amenda_lei', type: 'number', hot: true },
    { label: 'Puncte penalizare', path: 'sanctiune.puncte_penalizare', type: 'number' },
    { label: 'Suspendare permis (zile)', path: 'sanctiune.suspendare_zile', type: 'number' },
    { label: 'Martor asistent', path: 'martor_asistent.prezent', type: 'select-bool', hot: !extracted.martor_asistent.prezent },
    { label: 'Semnătura contravenientului', path: 'semnatura_contravenient', type: 'select-semn' },
  ];

  const handleChange = (path: string, value: unknown) => {
    if (!onChange) return;
    const next = setPath(extracted, path, value);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-baseline text-xs text-muted mono-caps">
        <span>Date extrase din PV · click pentru a corecta</span>
        <span>încredere {Math.round(confidence * 100)}%</span>
      </div>
      <div className="bg-card border border-line rounded-xl divide-y divide-line overflow-hidden">
        {rows.map((r) => (
          <EditableRow
            key={r.path}
            row={r}
            rawValue={getPath(extracted, r.path)}
            onChange={(v) => handleChange(r.path, v)}
            disabled={disabled || !onChange}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
//  EditableRow — click to edit, blur/Enter to commit
// =============================================================================

interface EditableRowProps {
  row: Row;
  rawValue: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

function EditableRow({ row, rawValue, onChange, disabled }: EditableRowProps) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current) inputRef.current.select();
    }
  }, [editing]);

  const display = formatDisplay(row, rawValue);
  const empty = rawValue === null || rawValue === undefined || rawValue === '';

  // Selects open inline immediately on click — no read mode needed.
  const isSelect = row.type === 'select-tip' || row.type === 'select-semn' || row.type === 'select-bool';

  return (
    <div
      className={[
        'flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4 px-4 py-3 sm:py-3.5 text-sm transition',
        !disabled && !editing && !isSelect ? 'cursor-text hover:bg-bg-2' : '',
      ].join(' ')}
      onClick={() => {
        if (disabled) return;
        if (!editing && !isSelect) setEditing(true);
      }}
    >
      <span className="text-muted sm:shrink-0 sm:w-44 text-xs sm:text-sm uppercase sm:normal-case tracking-wider sm:tracking-normal">
        {row.label}
      </span>

      <div className="flex-1 sm:text-right" onClick={(e) => editing && e.stopPropagation()}>
        {editing && !isSelect ? (
          <InlineEditor
            row={row}
            initialValue={rawValue}
            inputRef={inputRef}
            onCommit={(v) => {
              setEditing(false);
              if (v !== rawValue) onChange(v);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : isSelect ? (
          <SelectEditor
            row={row}
            value={rawValue}
            disabled={disabled}
            onChange={(v) => onChange(v)}
          />
        ) : (
          <span
            className={[
              'inline-block text-fg-2 break-words',
              empty ? 'text-faint italic' : '',
              row.hot ? 'font-medium' : '',
            ].join(' ')}
          >
            {display}
            {row.hot && !empty && <span className="ml-2 text-accent-dark">•</span>}
            {!disabled && !empty && (
              <span className="ml-2 text-[10px] font-mono text-faint uppercase tracking-wider">
                ✎
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function formatDisplay(row: Row, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'lipsește';
  if (row.path === 'data_emitere' && typeof value === 'string') {
    return fmtDate(value) ?? value;
  }
  if (row.type === 'select-bool') {
    return value === true ? 'prezent' : value === false ? 'absent' : 'lipsește';
  }
  if (row.type === 'number' && typeof value === 'number') {
    if (row.path === 'sanctiune.amenda_lei') return `${value.toLocaleString('ro-RO')} lei`;
    if (row.path === 'sanctiune.suspendare_zile') return `${value} zile`;
    return String(value);
  }
  return String(value);
}

// =============================================================================
//  InlineEditor — wraps the right control type for the row
// =============================================================================

interface InlineEditorProps {
  row: Row;
  initialValue: unknown;
  inputRef: React.MutableRefObject<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
  >;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
}

const inputBase =
  'block w-full bg-bg border border-fg rounded-md px-3 py-2 text-[14px] text-fg outline-none focus:ring-2 focus:ring-fg/15';

function InlineEditor({ row, initialValue, inputRef, onCommit, onCancel }: InlineEditorProps) {
  const [draft, setDraft] = useState(() => stringify(initialValue, row.type));

  const commit = () => {
    const parsed = parse(draft, row.type);
    onCommit(parsed);
  };

  if (row.type === 'textarea') {
    return (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        rows={3}
        className={`${inputBase} resize-y min-h-[80px] text-left`}
      />
    );
  }

  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type={row.type === 'number' ? 'number' : 'text'}
      inputMode={row.type === 'number' ? 'decimal' : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      className={`${inputBase} sm:text-right`}
    />
  );
}

function stringify(value: unknown, type: FieldType): string {
  if (value === null || value === undefined) return '';
  if (type === 'number' && typeof value === 'number') return String(value);
  return String(value);
}

function parse(raw: string, type: FieldType): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (type === 'number') {
    const n = Number(trimmed.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return trimmed;
}

// =============================================================================
//  SelectEditor — for enum and boolean fields, always shown as dropdown
// =============================================================================

interface SelectEditorProps {
  row: Row;
  value: unknown;
  disabled?: boolean;
  onChange: (value: unknown) => void;
}

function SelectEditor({ row, value, disabled, onChange }: SelectEditorProps) {
  const handleChange = (raw: string) => {
    if (raw === '__null__') return onChange(null);
    if (row.type === 'select-bool') {
      onChange(raw === 'true');
      return;
    }
    onChange(raw);
  };

  const stringValue =
    value === null || value === undefined
      ? '__null__'
      : row.type === 'select-bool'
        ? value === true
          ? 'true'
          : 'false'
        : String(value);

  let options: Array<{ value: string; label: string }>;
  if (row.type === 'select-tip') {
    options = TIP_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  } else if (row.type === 'select-semn') {
    options = SEMN_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  } else {
    options = [
      { value: 'true', label: 'prezent' },
      { value: 'false', label: 'absent' },
    ];
  }

  return (
    <select
      value={stringValue}
      disabled={disabled}
      onChange={(e) => handleChange(e.target.value)}
      className="bg-card border border-line rounded-md px-2 py-1 text-sm text-fg-2 focus:outline-none focus:border-fg sm:text-right"
    >
      <option value="__null__">lipsește</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
